/**
 * GA4 analytics helpers.
 *
 * The measurement ID is read at build time from REACT_APP_GA4_MEASUREMENT_ID.
 * If missing, all tracking calls are no-ops — safe to ship without an ID.
 *
 * PRIVACY:
 *  - Only public public-page analytics — nothing under /admin sends events.
 *  - No PII (email addresses, names, phone numbers, form field values) sent.
 *  - Events only carry categorical metadata (form_name, page path, cta_kind).
 */

const GA_ID = process.env.REACT_APP_GA4_MEASUREMENT_ID || "";

// GA is enabled when the gtag() function has been installed by the loader
// in index.html — the ID itself lives in the loader snippet.
export const gaEnabled = () => typeof window !== "undefined" && typeof window.gtag === "function";

// Do NOT track admin routes.
const isPublicPath = (path) => !String(path || "").startsWith("/admin") && !String(path || "").startsWith("/login");

export function trackPageView(path) {
  if (!gaEnabled() || !isPublicPath(path)) return;
  try {
    window.gtag("event", "page_view", {
      page_path: path,
      page_location: window.location.href,
      page_title: document.title,
    });
  } catch { /* silent */ }
}

// The 5 conversion event helpers the user requested.
export function trackGenerateLead(source) {
  if (!gaEnabled()) return;
  try { window.gtag("event", "generate_lead", { source: source || "unknown" }); } catch { /* */ }
}

export function trackPhoneClick(number_label) {
  if (!gaEnabled()) return;
  // number_label is a categorical marker like "header_cta" — NOT the actual phone number.
  try { window.gtag("event", "phone_click", { location: number_label || "unknown" }); } catch { /* */ }
}

export function trackEmailClick(location) {
  if (!gaEnabled()) return;
  try { window.gtag("event", "email_click", { location: location || "unknown" }); } catch { /* */ }
}

export function trackFormSubmit(form_name) {
  if (!gaEnabled()) return;
  try { window.gtag("event", "form_submit", { form_name: form_name || "unknown" }); } catch { /* */ }
}

export function trackAppointmentRequest(source) {
  if (!gaEnabled()) return;
  try { window.gtag("event", "appointment_request", { source: source || "unknown" }); } catch { /* */ }
}

// ── Client-portal preview beacons ──────────────────────────────────────
// Kit uses these to spot warm prospects: anyone who opens `/preview` is
// materially higher intent than a homepage bounce, and the depth signal
// tells him who scrolled the full dashboard vs bailed.

// Fires to both gtag (GA4) and posthog (session replay). Idempotent — call
// helpers won't crash if either provider is missing.
function _dualFire(eventName, params) {
  try { if (typeof window !== "undefined" && typeof window.gtag === "function") window.gtag("event", eventName, params); } catch { /* */ }
  try { if (typeof window !== "undefined" && window.posthog && typeof window.posthog.capture === "function") window.posthog.capture(eventName, params); } catch { /* */ }
}

export function trackPreviewView(source) {
  _dualFire("preview_view", { source: source || "direct" });
}

export function trackPreviewScrollDepth(depth) {
  // depth is one of 25 / 50 / 75 / 100
  _dualFire("preview_scroll_depth", { depth });
}

export function trackPreviewCtaClick(cta) {
  // cta: ribbon_book | floating_book | floating_call | close
  _dualFire("preview_cta_click", { cta: cta || "unknown" });
}

/**
 * Global click delegate — attaches ONCE at app boot. Automatically fires:
 *   - phone_click for any <a href="tel:...">
 *   - email_click for any <a href="mailto:...">
 *   - appointment_request for any element with data-analytics-book="true" OR
 *     buttons/links whose visible text matches /book|schedule|reserve/i
 * Idempotent — safe to call multiple times.
 */
let _installed = false;
export function installGlobalClickTracker() {
  if (_installed || typeof document === "undefined") return;
  _installed = true;
  document.addEventListener(
    "click",
    (e) => {
      if (!gaEnabled()) return;
      try {
        const el = e.target.closest("a, button, [role='button']");
        if (!el) return;
        const href = (el.getAttribute && el.getAttribute("href")) || "";
        const label = (el.dataset && el.dataset.analyticsLabel) || el.getAttribute("aria-label") || (el.innerText || "").trim().slice(0, 40);
        if (href.startsWith("tel:")) {
          trackPhoneClick(label || "phone-link");
          return;
        }
        if (href.startsWith("mailto:")) {
          trackEmailClick(label || "email-link");
          return;
        }
        // Book / schedule detection
        const bookAttr = el.dataset && (el.dataset.analyticsBook === "true" || el.dataset.analyticsEvent === "book");
        const bookText = /book\b|schedule\b|reserve\b|appointment\b/i.test(label);
        if (bookAttr || bookText) {
          trackAppointmentRequest(label || "book-cta");
        }
      } catch { /* */ }
    },
    { capture: true }
  );
}
